/**
 * Cloud-storage import adapters.
 *
 * Each provider's pickFromX() returns a Promise<File> so the caller can hand
 * the result to the existing chunked upload pipeline (uploadDocument(file))
 * without any other changes - provider auth, downloads, and format
 * conversions stay encapsulated here.
 *
 * Provider matrix:
 *   pickFromDropbox     - Dropbox Chooser (drop-in JS, no OAuth)
 *   pickFromGoogleDrive - Google Drive Picker (GIS OAuth + drive.file scope)
 *   pickFromOneDrive    - Microsoft File Picker v8 (MSAL + popup + postMessage)
 *
 * Why client-side download for all of these: the provider OAuth token (where
 * one exists) never leaves the browser. The existing `import-url` API endpoint
 * remains the API-side path for opaque cloud URLs (S3/GCS presigned, public
 * shares, etc.) - see fileName override on ImportDocumentFromUrlApiRequest.
 */

const DROPBOX_CHOOSER_SRC = 'https://www.dropbox.com/static/api/2/dropins.js';
const GOOGLE_GIS_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_GAPI_SRC = 'https://apis.google.com/js/api.js';
// drive.file is the right scope: access to files the user explicitly picks via
// our app, nothing else. Avoids the Restricted-scope CASA security assessment
// (drive.readonly et al.) while still letting Picker hand us the chosen file.
const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

// MSAL.js loaded from jsDelivr's npm mirror. Microsoft's alcdn.msauth.net is
// only reliable for older versions and doesn't host v4+ at all; jsDelivr
// mirrors npm directly and is fine for a public client identifier flow.
// Pinned to a specific tested version - rotate deliberately.
const MSAL_CDN_SRC = 'https://cdn.jsdelivr.net/npm/@azure/msal-browser@4.30.0/lib/msal-browser.min.js';
// "common" authority lets users sign in with either work/school OR personal
// MS accounts; we currently only complete the flow for work/school (see
// pickFromOneDrive - personal accounts hit a not-supported error). Adding
// personal is additive: different baseUrl (onedrive.live.com/picker), same
// picker page, same postMessage protocol.
const MSAL_AUTHORITY = 'https://login.microsoftonline.com/common';
// Per-request authority used when acquiring consumer OneDrive tokens. Per
// Microsoft's File Picker v8 consumer sample, scope aliases like
// OneDrive.ReadOnly only resolve correctly via the /consumers endpoint;
// /common refuses them with AADSTS70011 ("scope not configured for tenant").
const MSAL_AUTHORITY_CONSUMERS = 'https://login.microsoftonline.com/consumers';

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];

// Max file size, mirrored from the local q-file picker's max-file-size prop
// and the server's DocumentProcessingOptions.MaxFileSizeBytes default. Cloud
// imports must enforce the same cap pre-download so users don't pull large
// files across the network just to be rejected at chunked-upload commit time.
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / 1024 / 1024;

/**
 * Reject pre-download when the picker reports a size over the limit.
 * Picker size fields are sometimes strings (Google) and sometimes missing
 * (OneDrive picker doesn't guarantee `size`); we parse leniently and skip
 * the check when the value isn't usable - the defense-in-depth post-download
 * blob.size check still catches over-limit content in that case.
 */
function enforceMaxFileSize(name, rawSize) {
    const n = typeof rawSize === 'string' ? parseInt(rawSize, 10) : rawSize;
    if (typeof n === 'number' && Number.isFinite(n) && n > MAX_FILE_SIZE_BYTES) {
        const mb = (n / 1024 / 1024).toFixed(1);
        throw new Error(`"${name}" is ${mb} MB; the maximum is ${MAX_FILE_SIZE_MB} MB.`);
    }
}

// MIME types Google Drive surfaces. The first three are direct binary files
// we can fetch with `?alt=media`. The Google Docs native type is exported
// to PDF on the fly via the export endpoint. Sheets and Slides are
// intentionally excluded - signing use case is documents, not spreadsheets.
const GOOGLE_DRIVE_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.google-apps.document'
];
const GOOGLE_DOCS_MIME = 'application/vnd.google-apps.document';

let dropboxLoadPromise = null;

function loadDropboxChooser(appKey) {
    if (window.Dropbox && typeof window.Dropbox.choose === 'function') {
        return Promise.resolve();
    }
    if (dropboxLoadPromise) return dropboxLoadPromise;

    dropboxLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = DROPBOX_CHOOSER_SRC;
        script.id = 'dropboxjs';
        script.setAttribute('data-app-key', appKey);
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            dropboxLoadPromise = null;
            reject(new Error('Failed to load the Dropbox Chooser script'));
        };
        document.head.appendChild(script);
    });
    return dropboxLoadPromise;
}

function getExtension(name) {
    const idx = name.lastIndexOf('.');
    return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

/**
 * Open the Dropbox Chooser and return a File object for the selected file.
 *
 * Behavior: requests linkType='direct' so we receive a temporary direct
 * download URL, fetch the bytes in the browser, wrap them as a File, and
 * hand the File back. From the caller's perspective the result is
 * indistinguishable from a local file pick - the existing chunked upload
 * pipeline takes it as-is.
 *
 * Constraints: extensions filter mirrors the q-file accept list
 * (.pdf/.doc/.docx). Multiselect is disabled for v1 - adding it later just
 * means returning Promise<File[]>.
 *
 * @returns {Promise<File>} the selected file, or rejects if the user
 *   cancels, the script fails to load, or the download fails.
 */
export function pickFromDropbox() {
    const appKey = window._pageConfig?.cloudImports?.dropboxAppKey;
    if (!appKey) {
        return Promise.reject(new Error(
            'Dropbox import is not configured. Set PlatformSign:CloudImports:Dropbox:AppKey.'
        ));
    }

    return loadDropboxChooser(appKey).then(() => new Promise((resolve, reject) => {
        window.Dropbox.choose({
            success: async (files) => {
                const f = files && files[0];
                if (!f) {
                    reject(new Error('No file was selected'));
                    return;
                }
                const ext = getExtension(f.name);
                if (!ALLOWED_EXTENSIONS.includes(ext)) {
                    reject(new Error(
                        `Only PDF, DOC, and DOCX files are accepted (got "${f.name}")`
                    ));
                    return;
                }
                try {
                    enforceMaxFileSize(f.name, f.bytes);
                    const res = await fetch(f.link);
                    if (!res.ok) {
                        throw new Error(`Dropbox returned HTTP ${res.status}`);
                    }
                    const blob = await res.blob();
                    enforceMaxFileSize(f.name, blob.size);
                    const file = new File([blob], f.name, {
                        type: blob.type || 'application/octet-stream',
                        lastModified: Date.now()
                    });
                    resolve(file);
                } catch (err) {
                    reject(new Error(
                        `Failed to download from Dropbox: ${err.message || err}`
                    ));
                }
            },
            cancel: () => reject(new Error('cancelled')),
            linkType: 'direct',
            multiselect: false,
            extensions: ALLOWED_EXTENSIONS
        });
    }));
}

/**
 * True iff the Dropbox import is enabled for this page (i.e. the platform
 * app key is configured). Used to gate the "From Dropbox" button so it
 * stays hidden in environments without the key configured rather than
 * presenting a button that always errors.
 */
export function isDropboxImportEnabled() {
    return !!window._pageConfig?.cloudImports?.dropboxAppKey;
}

// ───────────────────────── Google Drive ─────────────────────────

let gisLoadPromise = null;
let gapiLoadPromise = null;
let pickerLoadPromise = null;

function loadScriptOnce(src, idAttr) {
    return new Promise((resolve, reject) => {
        if (idAttr && document.getElementById(idAttr)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        if (idAttr) s.id = idAttr;
        s.src = src;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(s);
    });
}

function loadGoogleIdentityServices() {
    if (window.google?.accounts?.oauth2) return Promise.resolve();
    if (gisLoadPromise) return gisLoadPromise;
    gisLoadPromise = loadScriptOnce(GOOGLE_GIS_SRC, 'gsi-client').catch(err => {
        gisLoadPromise = null;
        throw err;
    });
    return gisLoadPromise;
}

function loadGapi() {
    if (window.gapi) return Promise.resolve();
    if (gapiLoadPromise) return gapiLoadPromise;
    gapiLoadPromise = loadScriptOnce(GOOGLE_GAPI_SRC, 'gapi-client').catch(err => {
        gapiLoadPromise = null;
        throw err;
    });
    return gapiLoadPromise;
}

function loadPickerModule() {
    if (window.google?.picker) return Promise.resolve();
    if (pickerLoadPromise) return pickerLoadPromise;
    pickerLoadPromise = loadGapi().then(() => new Promise((resolve, reject) => {
        window.gapi.load('picker', {
            callback: () => resolve(),
            onerror: () => {
                pickerLoadPromise = null;
                reject(new Error('Failed to load the Google Picker module'));
            }
        });
    }));
    return pickerLoadPromise;
}

function requestGoogleAccessToken(clientId) {
    return new Promise((resolve, reject) => {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: GOOGLE_DRIVE_SCOPE,
            callback: (response) => {
                if (response.error) {
                    reject(new Error(`Google sign-in failed: ${response.error}`));
                    return;
                }
                resolve(response.access_token);
            },
            error_callback: (err) => {
                // user closed the consent popup
                if (err?.type === 'popup_closed' || err?.type === 'popup_failed_to_open') {
                    reject(new Error('cancelled'));
                    return;
                }
                reject(new Error(err?.message || 'Google sign-in failed'));
            }
        });
        // Use prompt: '' to skip consent if the user has already granted scope this session.
        tokenClient.requestAccessToken({ prompt: '' });
    });
}

function showPicker({ accessToken, apiKey, appId }) {
    return new Promise((resolve, reject) => {
        const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
            .setMimeTypes(GOOGLE_DRIVE_MIME_TYPES.join(','))
            .setIncludeFolders(false)
            .setSelectFolderEnabled(false);

        // setAppId is REQUIRED for drive.file scope. Without it, the picker
        // gives the user a chooser but doesn't tag the selected file with
        // per-app authorization, so the subsequent Drive API GET returns 404.
        // appId is the GCP project number (the numeric prefix of the OAuth
        // client ID).
        const picker = new window.google.picker.PickerBuilder()
            .addView(view)
            .setOAuthToken(accessToken)
            .setDeveloperKey(apiKey)
            .setAppId(appId)
            .setMaxItems(1)
            .setCallback((data) => {
                const Action = window.google.picker.Action;
                if (data.action === Action.PICKED) {
                    const doc = data.docs && data.docs[0];
                    if (!doc) {
                        reject(new Error('No file was selected'));
                        return;
                    }
                    resolve(doc);
                } else if (data.action === Action.CANCEL) {
                    reject(new Error('cancelled'));
                }
            })
            .build();
        picker.setVisible(true);
    });
}

async function downloadFromGoogleDrive(driveDoc, accessToken) {
    const isNativeGoogleDoc = driveDoc.mimeType === GOOGLE_DOCS_MIME;

    let url, downloadName, downloadMime;
    if (isNativeGoogleDoc) {
        // Export Google Docs natively to PDF - Drive API handles the conversion.
        url = `https://www.googleapis.com/drive/v3/files/${driveDoc.id}/export?mimeType=application%2Fpdf`;
        downloadName = `${driveDoc.name}.pdf`;
        downloadMime = 'application/pdf';
    } else {
        url = `https://www.googleapis.com/drive/v3/files/${driveDoc.id}?alt=media`;
        // Picker's `name` already includes the file extension for binary files.
        downloadName = driveDoc.name;
        downloadMime = driveDoc.mimeType;
    }

    // Pre-download check from picker metadata (best-effort - sizeBytes
    // reflects source-doc size for Google Docs, not the resulting PDF, but
    // it's a reasonable proxy and saves bandwidth on grossly oversized files).
    enforceMaxFileSize(downloadName, driveDoc.sizeBytes);

    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (!res.ok) {
        throw new Error(`Google Drive returned HTTP ${res.status}`);
    }

    const blob = await res.blob();
    enforceMaxFileSize(downloadName, blob.size);
    const ext = getExtension(downloadName);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
        throw new Error(`Only PDF, DOC, and DOCX files are accepted (got "${downloadName}")`);
    }

    return new File([blob], downloadName, {
        type: blob.type || downloadMime || 'application/octet-stream',
        lastModified: Date.now()
    });
}

/**
 * Open the Google Drive Picker and return a File object for the selected file.
 *
 * Behavior: prompts the user for OAuth consent (drive.file scope - narrow,
 * grants access only to files the user explicitly picks), opens the Picker
 * with a Drive view filtered to PDF/DOC/DOCX/Google Docs, downloads the
 * picked file's bytes via the Drive v3 API, wraps as a File. Google Docs
 * native files are exported to PDF on the fly.
 *
 * The OAuth token is held in memory for this user gesture only - never
 * persisted, never sent to our server. Each click triggers a fresh token
 * (skipping the consent screen if the browser still has a session-level
 * grant for our app).
 *
 * @returns {Promise<File>} the selected file, or rejects with `cancelled`
 *   if the user dismisses the consent popup or the Picker.
 */
export async function pickFromGoogleDrive() {
    const cfg = window._pageConfig?.cloudImports;
    const clientId = cfg?.googleClientId;
    const apiKey = cfg?.googleApiKey;
    if (!clientId || !apiKey) {
        throw new Error(
            'Google Drive import is not configured. Set PlatformSign:CloudImports:Google:ClientId and ApiKey.'
        );
    }
    // GCP project number for the Picker's setAppId - the numeric prefix of
    // the OAuth client ID (everything before the first hyphen). Required
    // for drive.file-scope picker-to-Drive-API authorization.
    const appId = clientId.split('-')[0];

    await Promise.all([loadGoogleIdentityServices(), loadPickerModule()]);

    const accessToken = await requestGoogleAccessToken(clientId);
    const driveDoc = await showPicker({ accessToken, apiKey, appId });
    return await downloadFromGoogleDrive(driveDoc, accessToken);
}

/**
 * True iff Google Drive import is enabled for this page (both the OAuth
 * client ID and the Picker API key are configured). Same fail-closed UX as
 * Dropbox: button stays hidden until configuration lands.
 */
export function isGoogleDriveImportEnabled() {
    const cfg = window._pageConfig?.cloudImports;
    return !!(cfg?.googleClientId && cfg?.googleApiKey);
}

// ───────────────────────── OneDrive / SharePoint ─────────────────────────
//
// File Picker v8 (https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/).
// Microsoft maintains two distinct identity + storage backends behind a shared
// MSAL login surface; we branch on account type after login:
//
//   work / school (M365) →  pickerUrl = {tenant}-my.sharepoint.com/_layouts/15/FilePicker.aspx
//                           pickerScope = {baseUrl}/.default (a SharePoint resource token)
//                           graphScopes = ['Files.Read.All', 'Sites.Read.All']
//                           baseUrl discovered from Graph /me/drive
//
//   personal MS account →   pickerUrl = onedrive.live.com/picker (constant)
//                           pickerScope = OneDrive.ReadOnly (a Live OneDrive scope)
//                           graphScopes = ['Files.Read'] (consumer Graph)
//                           no host discovery needed
//
// Microsoft Graph's /drives/{id}/items/{id}/content endpoint works uniformly for
// BOTH after the right token is in hand - that's the one piece they did unify.
// The postMessage+MessageChannel protocol is identical for both backends.

let msalLoadPromise = null;
let msalInstancePromise = null;

function loadMsal() {
    if (window.msal?.PublicClientApplication) return Promise.resolve();
    if (msalLoadPromise) return msalLoadPromise;
    msalLoadPromise = loadScriptOnce(MSAL_CDN_SRC, 'msal-browser').catch(err => {
        msalLoadPromise = null;
        throw err;
    });
    return msalLoadPromise;
}

function getMsalInstance(clientId) {
    // Singleton promise pattern - all concurrent callers (prewarm + click)
    // await the same in-flight init, so we never construct two
    // PublicClientApplications or call initialize() twice.
    if (msalInstancePromise) return msalInstancePromise;
    msalInstancePromise = (async () => {
        await loadMsal();
        const instance = new window.msal.PublicClientApplication({
            auth: {
                clientId,
                authority: MSAL_AUTHORITY,
                redirectUri: window.location.origin
            },
            cache: { cacheLocation: 'sessionStorage' }
        });
        await instance.initialize();
        return instance;
    })().catch(err => {
        msalInstancePromise = null; // allow retry on next call
        throw err;
    });
    return msalInstancePromise;
}

async function ensureMsalAccount(msal) {
    const accounts = msal.getAllAccounts();
    if (accounts.length > 0) {
        msal.setActiveAccount(accounts[0]);
        return accounts[0];
    }
    // First-time login - minimal Graph scope to identify the user. The picker
    // resource token is acquired separately once we know the SharePoint host.
    const result = await msal.loginPopup({
        scopes: ['User.Read', 'Files.Read.All']
    });
    msal.setActiveAccount(result.account);
    return result.account;
}

function isPersonalMicrosoftAccount(account) {
    // The consumer tenant id Microsoft uses for personal MSA accounts.
    return account?.tenantId === '9188040d-6c67-4c5b-b112-36a304b66dad';
}

async function acquireToken(msal, scopes, authority) {
    const request = authority ? { scopes, authority } : { scopes };
    try {
        const result = await msal.acquireTokenSilent(request);
        return result.accessToken;
    } catch {
        const result = await msal.acquireTokenPopup(request);
        return result.accessToken;
    }
}

async function discoverSharePointBaseUrl(graphToken) {
    // Graph /me/drive returns the user's OneDrive (a special document library
    // backed by their SharePoint personal site). webUrl is e.g.
    // "https://contoso-my.sharepoint.com/personal/john_contoso_com/Documents".
    // We parse out the host portion as the picker baseUrl.
    const res = await fetch('https://graph.microsoft.com/v1.0/me/drive', {
        headers: { Authorization: `Bearer ${graphToken}` }
    });
    if (!res.ok) {
        throw new Error(`Could not discover OneDrive site (HTTP ${res.status}). The signed-in account may not have OneDrive provisioned.`);
    }
    const drive = await res.json();
    const webUrl = drive?.webUrl;
    if (!webUrl) throw new Error('OneDrive web URL not returned by Microsoft Graph.');
    const u = new URL(webUrl);
    return `${u.protocol}//${u.host}`;
}

/**
 * Resolves the picker URL, picker resource scope, and Graph download scopes
 * for the current account. The two account types have different infrastructure
 * (see header comment above). Returns enough to drive both the picker popup
 * AND the post-pick file download.
 */
async function resolveOneDriveContext(msal, account) {
    if (isPersonalMicrosoftAccount(account)) {
        // Per Microsoft's File Picker v8 consumer sample: requests must use
        // the /consumers authority explicitly so OneDrive.ReadOnly aliases to
        // the Graph Files.Read permission (which we have in the app reg).
        // /common doesn't auto-resolve the alias and returns AADSTS70011.
        return {
            kind: 'personal',
            pickerUrl: 'https://onedrive.live.com/picker',
            pickerScopes: ['OneDrive.ReadOnly'],
            graphScopes: ['Files.Read'],
            authority: MSAL_AUTHORITY_CONSUMERS
        };
    }
    // Work/school: discover the user's SharePoint personal-site baseUrl from
    // their OneDrive entry, derive picker URL + resource-bound picker scope.
    const graphToken = await acquireToken(msal, ['Files.Read.All', 'Sites.Read.All']);
    const baseUrl = await discoverSharePointBaseUrl(graphToken);
    return {
        kind: 'business',
        pickerUrl: `${baseUrl}/_layouts/15/FilePicker.aspx`,
        pickerScopes: [`${baseUrl}/.default`],
        graphScopes: ['Files.Read.All', 'Sites.Read.All'],
        graphToken  // already acquired during baseUrl discovery, save a round-trip
    };
}

function buildPickerOptions(channelId) {
    return {
        sdk: '8.0',
        entry: { oneDrive: {} },
        authentication: {},
        messaging: { origin: window.location.origin, channelId },
        typesAndSources: {
            mode: 'files',
            // Hint to the picker about which file types are sensible - UI hint
            // only, server-side magic-byte check is the authoritative gate.
            filters: ['.pdf', '.doc', '.docx']
        },
        selection: { mode: 'single' }
    };
}

function openPickerPopup(pickerUrl, options, accessToken) {
    const win = window.open('', 'OneDrivePicker', 'width=1080,height=680');
    if (!win) {
        throw new Error('Popup blocked. Allow popups for this site to use OneDrive import.');
    }
    const queryString = new URLSearchParams({
        filePicker: JSON.stringify(options),
        locale: 'en-us'
    });
    const url = `${pickerUrl}?${queryString}`;

    const form = win.document.createElement('form');
    form.setAttribute('action', url);
    form.setAttribute('method', 'POST');

    const tokenInput = win.document.createElement('input');
    tokenInput.setAttribute('type', 'hidden');
    tokenInput.setAttribute('name', 'access_token');
    tokenInput.setAttribute('value', accessToken);
    form.appendChild(tokenInput);

    win.document.body.append(form);
    form.submit();
    return win;
}

async function downloadOneDriveItem(item, graphToken) {
    // The picker returns { id, parentReference: { driveId }, name, ... }. Use
    // Graph's drive items download endpoint - stable URL, well-documented,
    // works regardless of whether the item lives in OneDrive or a SharePoint
    // site (drive abstraction collapses both).
    const driveId = item.parentReference?.driveId;
    const itemId = item.id;
    if (!driveId || !itemId) {
        throw new Error('Picked item missing drive or item ID');
    }
    const name = item.name || 'download';

    // Pre-download check from picker metadata. The Microsoft File Picker v8
    // doesn't guarantee `size` on every picked item, so this is best-effort -
    // the post-download blob.size check below is the definitive guard.
    enforceMaxFileSize(name, item.size);

    const url = `https://graph.microsoft.com/v1.0/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${graphToken}` }
    });
    if (!res.ok) {
        throw new Error(`OneDrive returned HTTP ${res.status}`);
    }
    const blob = await res.blob();
    enforceMaxFileSize(name, blob.size);
    const ext = getExtension(name);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
        throw new Error(`Only PDF, DOC, and DOCX files are accepted (got "${name}")`);
    }
    return new File([blob], name, {
        type: blob.type || 'application/octet-stream',
        lastModified: Date.now()
    });
}

/**
 * Open the OneDrive / SharePoint File Picker v8 and return a File for the
 * selected file. Supports BOTH work/school Microsoft 365 accounts AND
 * personal Microsoft accounts - the underlying Microsoft infrastructure is
 * split (see header comment) but the user-facing flow is identical.
 *
 * Flow: MSAL login (common authority lets either account type sign in) →
 * resolve account-type-specific picker URL + scopes → acquire picker resource
 * token → open popup → POST form to picker page → set up MessagePort → handle
 * authenticate / pick / close commands → download via Graph → wrap as File.
 * The OAuth tokens stay in browser memory - never reach our server.
 *
 * @returns {Promise<File>} the picked file, or rejects with `cancelled` if
 *   the user closes the popup without picking.
 */
export async function pickFromOneDrive() {
    const clientId = window._pageConfig?.cloudImports?.microsoftClientId;
    if (!clientId) {
        throw new Error(
            'OneDrive import is not configured. Set PlatformSign:CloudImports:Microsoft:ClientId.'
        );
    }

    const msal = await getMsalInstance(clientId);
    const account = await ensureMsalAccount(msal);

    const ctx = await resolveOneDriveContext(msal, account);
    // Graph token: reuse the one acquired during baseUrl discovery for
    // business accounts; acquire a consumer-Graph token for personal accounts.
    // Personal accounts override the authority to /consumers per Microsoft's
    // File Picker v8 consumer sample.
    const graphToken = ctx.graphToken ?? await acquireToken(msal, ctx.graphScopes, ctx.authority);
    const pickerToken = await acquireToken(msal, ctx.pickerScopes, ctx.authority);

    const channelId = (crypto.randomUUID && crypto.randomUUID()) ||
        `chan-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    const options = buildPickerOptions(channelId);

    const popup = openPickerPopup(ctx.pickerUrl, options, pickerToken);

    return await new Promise((resolve, reject) => {
        let port = null;
        let resolved = false;
        let popupClosedPoll = null;

        const cleanup = () => {
            window.removeEventListener('message', onWindowMessage);
            if (port) port.close();
            if (popupClosedPoll) clearInterval(popupClosedPoll);
            try { popup.close(); } catch { /* may already be closed */ }
        };

        const finish = (file) => {
            if (resolved) return;
            resolved = true;
            cleanup();
            resolve(file);
        };

        const fail = (err) => {
            if (resolved) return;
            resolved = true;
            cleanup();
            reject(err);
        };

        // Detect user closing the popup without picking - postMessage protocol
        // doesn't always fire a `close` notification in that case.
        popupClosedPoll = setInterval(() => {
            if (popup.closed) {
                fail(new Error('cancelled'));
            }
        }, 500);

        const onPortMessage = async (msg) => {
            const payload = msg.data;
            if (payload.type === 'notification') {
                return; // page-loaded, etc - informational only
            }
            if (payload.type !== 'command') return;

            // Every command must be acknowledged.
            port.postMessage({ type: 'acknowledge', id: msg.data.id });

            const cmd = payload.data;
            switch (cmd.command) {
                case 'authenticate': {
                    try {
                        // Picker can request fresh tokens mid-session. For business
                        // accounts the picker may legitimately ask for a different
                        // SharePoint site resource (e.g. user navigates from
                        // -my.sharepoint.com to a team site) - honour cmd.resource
                        // with the .default convention. For personal accounts the
                        // resource is fixed (consumer OneDrive), so re-issue the
                        // same scopes we configured up front.
                        const scopes = ctx.kind === 'personal'
                            ? ctx.pickerScopes
                            : [`${cmd.resource}/.default`];
                        const token = await acquireToken(msal, scopes, ctx.authority);
                        port.postMessage({
                            type: 'result',
                            id: msg.data.id,
                            data: { result: 'token', token }
                        });
                    } catch (e) {
                        port.postMessage({
                            type: 'result',
                            id: msg.data.id,
                            data: {
                                result: 'error',
                                error: { code: 'unableToObtainToken', message: e.message }
                            }
                        });
                    }
                    break;
                }
                case 'pick': {
                    try {
                        const items = cmd.items || [];
                        const item = items[0];
                        if (!item) throw new Error('No item picked');

                        const file = await downloadOneDriveItem(item, graphToken);

                        port.postMessage({
                            type: 'result',
                            id: msg.data.id,
                            data: { result: 'success' }
                        });
                        finish(file);
                    } catch (e) {
                        port.postMessage({
                            type: 'result',
                            id: msg.data.id,
                            data: {
                                result: 'error',
                                error: { code: 'unusableItem', message: e.message }
                            }
                        });
                        fail(e);
                    }
                    break;
                }
                case 'close': {
                    fail(new Error('cancelled'));
                    break;
                }
                default: {
                    port.postMessage({
                        type: 'result',
                        id: msg.data.id,
                        data: {
                            result: 'error',
                            error: { code: 'unsupportedCommand', message: cmd.command }
                        }
                    });
                    break;
                }
            }
        };

        const onWindowMessage = (event) => {
            // Validate sender is our popup; the picker hosts itself on a
            // SharePoint origin we don't pin (depends on the user's tenant),
            // so we authenticate via window-source identity not origin string.
            if (event.source !== popup) return;
            const message = event.data;
            if (message?.type === 'initialize' && message.channelId === channelId) {
                port = event.ports[0];
                port.addEventListener('message', onPortMessage);
                port.start();
                port.postMessage({ type: 'activate' });
            }
        };

        window.addEventListener('message', onWindowMessage);
    });
}

/**
 * True iff OneDrive / SharePoint import is configured (Microsoft Entra ID
 * client ID present). Personal Microsoft account support is not yet wired
 * but the toggle is the same - adding personal later is additive.
 */
export function isOneDriveImportEnabled() {
    return !!window._pageConfig?.cloudImports?.microsoftClientId;
}

/**
 * Fire-and-forget pre-warm of MSAL.js so the click-to-window.open() chain
 * stays short enough to survive Chrome's transient-user-activation window.
 *
 * Why this exists: MSAL v4's loginPopup() chains script-load + new
 * PublicClientApplication() + initialize() + popup-open. With those done
 * lazily on the first click, the chain is too long and Chrome's popup
 * blocker fires (popup_window_error). Pre-warming on Vue component mount
 * means the click handler does only the final loginPopup() after a
 * cached-instance lookup.
 *
 * Idempotent and safe to call multiple times: getMsalInstance internally
 * caches both the loaded script and the initialized instance.
 *
 * Errors are swallowed (logged, not thrown) - the click handler will
 * surface any real failure when the user actually tries to import.
 */
export function prewarmOneDrive() {
    if (!isOneDriveImportEnabled()) return;
    const clientId = window._pageConfig.cloudImports.microsoftClientId;
    getMsalInstance(clientId).catch(err => {
        console.warn('[cloudImports] MSAL prewarm failed (will retry on click):', err);
    });
}
